/**
 * Metadata structure for FreePhoto.
 * Matches GeoPosition from react-native-geolocation-service:
 *   location.coords.latitude / longitude / accuracy / altitude
 *   location.timestamp  (ms epoch)
 */
export interface FreePhotoMetadata {
  location?: {
    coords?: {
      latitude: number;
      longitude: number;
      accuracy?: number;
      altitude?: number | null;
    };
    timestamp?: number;
  };
  compassHeading?: number; // 0-360 degrees
  address?: string;
  deviceInfo?: {
    model?: string;
    os?: string;
  };
  capturedAt?: string; // ISO timestamp
}

/**
 * Parse metadata JSON string to typed object
 * @param metadataString - JSON string from FreePhoto.metadata
 * @returns Parsed metadata object or null if invalid
 */
export const parseMetadata = (metadataString: string): FreePhotoMetadata | null => {
  try {
    return JSON.parse(metadataString);
  } catch (error) {
    console.error('Failed to parse metadata:', error);
    return null;
  }
};

/**
 * Format compass heading to human-readable string
 * @param degrees - Compass heading in degrees (0-360)
 * @returns Formatted string like "45° NE"
 */
const DIRECTIONS_VI = ['Bắc', 'Đông Bắc', 'Đông', 'Đông Nam', 'Nam', 'Tây Nam', 'Tây', 'Tây Bắc'];

/** Normalise any degree value (including negative) to [0, 360) */
const normalizeDegrees = (degrees: number): number => ((degrees % 360) + 360) % 360;

export const formatCompassHeading = (degrees?: number): string => {
  if (degrees === undefined || degrees === null) return 'N/A';

  const normalized = normalizeDegrees(degrees);
  const index = Math.round(normalized / 45) % 8;
  return `${DIRECTIONS_VI[index]} (${Math.round(normalized)}°)`;
};

/**
 * Get compass direction icon based on heading
 * @param degrees - Compass heading in degrees (0-360)
 * @returns Unicode arrow character
 */
export const getCompassIcon = (degrees?: number): string => {
  if (degrees === undefined || degrees === null) return '↑';

  const icons = ['↑', '↗', '→', '↘', '↓', '↙', '←', '↖'];
  const normalized = normalizeDegrees(degrees);
  const index = Math.round(normalized / 45) % 8;
  return icons[index];
};

/**
 * Get API base URL — same as the backend API (serves photo files via download endpoint)
 */
const getApiBaseUrl = (): string => {
  return import.meta.env.VITE_API_URL_ADMIN || 'http://localhost:11000';
};

/**
 * Generate download URL for a photo using backend endpoint
 * @param uuid - UUID of the photo
 * @returns Full URL to download the photo file
 */
export const getPhotoDownloadUrl = (uuid?: string): string => {
  if (!uuid) return '/placeholder.png';
  return `${getApiBaseUrl()}/api/geocollect/restore/download-free-photo-file/${uuid}`;
};

/**
 * Generate thumbnail URL for a photo
 * @param uuid - UUID of the photo
 * @returns Full URL to thumbnail image
 */
export const getThumbnailUrl = (uuid?: string): string => {
  return getPhotoDownloadUrl(uuid);
};

/**
 * Generate full image URL for a photo
 * @param uuid - UUID of the photo
 * @returns Full URL to full-size image
 */
export const getFullImageUrl = (uuid?: string): string => {
  return getPhotoDownloadUrl(uuid);
};

/**
 * Format date to readable string
 * @param dateString - ISO date string
 * @returns Formatted date string
 */
export const formatPhotoDate = (dateString?: string): string => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Format date and time to readable string
 * @param dateString - ISO date string
 * @returns Formatted date and time string
 */
export const formatPhotoDateTime = (dateString?: string): string => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Extract location text from metadata address
 * @param metadata - Parsed metadata object
 * @returns Short location string
 */
export const getLocationText = (metadata: FreePhotoMetadata | null): string => {
  if (!metadata) return 'Unknown';
  if (metadata.address) return metadata.address;
  const lat = metadata.location?.coords?.latitude;
  const lng = metadata.location?.coords?.longitude;
  if (lat != null && lng != null) {
    return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
  }
  return 'Unknown';
};

/**
 * Format GPS coordinates for display
 * @param lat - Latitude
 * @param lng - Longitude
 * @returns Formatted coordinate string
 */
export const formatCoordinates = (lat: number, lng: number): string => {
  return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
};

/**
 * Truncate text to specified length
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};
